"use client";

import { useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarClock,
  ChartColumn,
  Compass,
  Flame,
  Gauge,
  Plus,
  UsersRound,
} from "lucide-react";
import {
  inScope,
  iso,
  scopeLabel,
  targetEmployees,
  teamIndex,
  ALL_SCOPE,
  STATUS_META,
  STATUS_ORDER,
  type Scope,
} from "@routecore/core";
import { useStudio } from "@routecore/app-shell";
import { ScopeFilter, KpiCard, PeriodNav, PrimaryButton, Segmented } from "@routecore/controls";
import DatePicker from "@routecore/date-picker";
import { RollingNumber, Tilt3D } from "@routecore/motion-fx";

type Period = "month" | "week" | "day";

const AXIS_TICK = { fontSize: 11, fill: "#756A56" } as const;

export default function ReportsView() {
  const {
    employees,
    teams,
    itineraries,
    openItineraryModal,
    openEmployeeModal,
    openTeamModal,
    isPrivileged,
    canEditEmployee,
  } = useStudio();
  const [period, setPeriod] = useState<Period>("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [scope, setScope] = useState<Scope>(ALL_SCOPE);
  const teamOf = useMemo(() => teamIndex(employees), [employees]);
  const anyWritable = useMemo(
    () => isPrivileged || employees.some((e) => canEditEmployee(e)),
    [employees, isPrivileged, canEditEmployee],
  );

  const { start, end, label } = useMemo(() => {
    if (period === "month") {
      return {
        start: startOfMonth(cursor),
        end: endOfMonth(cursor),
        label: format(cursor, "MMMM yyyy"),
      };
    }
    if (period === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return { start: s, end: e, label: `${format(s, "MMM d")} – ${format(e, "MMM d")}` };
    }
    return {
      start: startOfDay(cursor),
      end: endOfDay(cursor),
      label: format(cursor, "EEE, MMM d"),
    };
  }, [period, cursor]);

  const filtered = useMemo(() => {
    const s = iso(start);
    const e = iso(end);
    return itineraries.filter((i) => i.date >= s && i.date <= e && inScope(i, scope, teamOf));
  }, [itineraries, start, end, scope, teamOf]);

  /** Activity dots for the mini calendar — respects the active team/employee scope. */
  const dayMarkers = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of itineraries) {
      if (!inScope(it, scope, teamOf)) continue;
      m.set(it.date, (m.get(it.date) ?? 0) + 1);
    }
    return m;
  }, [itineraries, scope, teamOf]);

  const perDay = useMemo(() => {
    return eachDayOfInterval({ start, end }).map((d) => {
      const key = iso(d);
      const count = filtered.filter((i) => i.date === key).length;
      return {
        key,
        tick: period === "week" ? format(d, "EEE d") : format(d, "d"),
        full: format(d, "EEEE, MMM d"),
        count,
      };
    });
  }, [start, end, filtered, period]);

  const statusMix = useMemo(
    () =>
      STATUS_ORDER.map((s) => ({
        key: s,
        name: STATUS_META[s].label,
        value: filtered.filter((i) => i.status === s).length,
        color: STATUS_META[s].color,
      })),
    [filtered],
  );

  const byEmployee = useMemo(() => {
    return targetEmployees(employees, scope)
      .map((e) => ({
        name: e.name.split(" ")[0] + " " + (e.name.split(" ")[1]?.[0] ? e.name.split(" ")[1][0] + "." : ""),
        full: e.name,
        count: filtered.filter((i) => i.employeeId === e.id).length,
        fill: e.color,
      }))
      .sort((a, b) => b.count - a.count);
  }, [employees, filtered, scope]);

  const kpis = useMemo(() => {
    const total = filtered.length;
    const completed = filtered.filter((i) => i.status === "completed").length;
    const active = new Set(filtered.map((i) => i.employeeId)).size;
    let busiest = perDay[0] ?? null;
    for (const d of perDay) if (d.count > (busiest?.count ?? 0)) busiest = d;
    return {
      total: String(total),
      completion: total ? `${Math.round((completed / total) * 100)}%` : "—",
      completionSub: `${completed} wrapped up`,
      active: String(active),
      busiest: busiest && busiest.count > 0 ? `${busiest.count}` : "—",
      busiestSub:
        busiest && busiest.count > 0 ? `peak on ${busiest.full}` : "no peak in range",
    };
  }, [filtered, perDay]);

  const step = (dir: 1 | -1) => {
    if (period === "month") setCursor((c) => addMonths(c, dir));
    else if (period === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addDays(c, dir));
  };

  const empty = filtered.length === 0;

  return (
    <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-none">
      {/* header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ChartColumn className="size-3.5 text-accent" />
            <span className="text-[10px] font-bold tracking-[0.24em] text-ink-faint uppercase">
              Reports · {scopeLabel(scope, teams, employees)}
            </span>
          </div>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            The numbers<span className="text-ink-faint">, mapped.</span>
          </h1>
        </div>
        <PrimaryButton
          icon={Plus}
          disabled={!anyWritable}
          onClick={() => openItineraryModal({})}
        >
          {anyWritable ? "New itinerary" : "🔒 No bound profile"}
        </PrimaryButton>
      </header>

      {/* controls */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Segmented<Period>
          id="reports-period"
          value={period}
          onChange={(v) => setPeriod(v)}
          options={[
            { value: "month", label: "Month" },
            { value: "week", label: "Week" },
            { value: "day", label: "Day" },
          ]}
        />
        {period === "day" ? (
          <DatePicker value={cursor} onChange={setCursor} markers={dayMarkers} />
        ) : (
          <PeriodNav
            label={label}
            onPrev={() => step(-1)}
            onNext={() => step(1)}
            onReset={() => setCursor(new Date())}
          />
        )}
      </div>
      <div className="mt-3">
        <ScopeFilter
          employees={employees}
          teams={teams}
          scope={scope}
          onChange={setScope}
          onAdd={isPrivileged ? () => openEmployeeModal() : undefined}
          onManageTeams={isPrivileged ? openTeamModal : undefined}
        />
      </div>

      {/* KPIs */}
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4 2xl:gap-4">
        <KpiCard icon={CalendarClock} label="Assignments" value={kpis.total} sub={`in ${label.toLowerCase()}`} accent="#E4572E" delay={0.1} />
        <KpiCard icon={Gauge} label="Completion" value={kpis.completion} sub={kpis.completionSub} accent="#1F8A70" delay={0.2} />
        <KpiCard icon={UsersRound} label="Crew active" value={kpis.active} sub={`of ${employees.length} on roster`} accent="#4C5FD5" delay={0.3} />
        <KpiCard icon={Flame} label="Busiest day" value={kpis.busiest} sub={kpis.busiestSub} accent="#D69E2E" delay={0.4} />
      </div>

      {empty ? (
        <div className="mt-6 grid place-items-center gap-3 rounded-2xl border border-dashed border-line bg-porcelain/60 py-20 text-center">
          <span className="grid size-12 place-items-center rounded-2xl bg-cream text-ink-faint">
            <Compass className="size-5" />
          </span>
          <p className="font-display text-xl text-ink-soft italic">
            Nothing plotted in this slice of time.
          </p>
          <button
            type="button"
            onClick={() => setCursor(new Date())}
            className="text-xs font-bold text-accent underline-offset-4 hover:underline"
          >
            Jump back to today
          </button>
        </div>
      ) : (
        <>
          {/* charts row */}
          <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-5">
            {/* volume */}
            <Tilt3D radius="1rem" intensity={4} lift={-4} scale={1.006} className="xl:col-span-3">
            <section className="h-full rounded-2xl border border-line bg-porcelain p-5 shadow-card">
              <ChartTitle
                title={period === "day" ? "Assignments by employee" : "Momentum"}
                sub={
                  period === "day"
                    ? label
                    : `Daily volume · ${label}`
                }
              />
              <div className="mt-4 h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  {period === "day" ? (
                    <BarChart data={byEmployee} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#E8E0CD" strokeDasharray="3 6" />
                      <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: "#E2D9C6" }} tick={AXIS_TICK} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={AXIS_TICK} />
                      <Tooltip cursor={{ fill: "rgba(34,29,21,0.04)" }} content={<Tip />} />
                      <Bar dataKey="count" radius={[7, 7, 0, 0]} maxBarSize={46}>
                        {byEmployee.map((d) => (
                          <Cell key={d.full} fill={d.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : (
                    <BarChart data={perDay} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#E8E0CD" strokeDasharray="3 6" />
                      <XAxis
                        dataKey="tick"
                        tickLine={false}
                        axisLine={{ stroke: "#E2D9C6" }}
                        tick={AXIS_TICK}
                        interval={period === "month" ? 2 : 0}
                      />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={AXIS_TICK} />
                      <Tooltip cursor={{ fill: "rgba(34,29,21,0.04)" }} content={<Tip labelKey="full" />} />
                      <Bar dataKey="count" radius={[7, 7, 0, 0]} fill="#E4572E" maxBarSize={30} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </section>
          </Tilt3D>

            {/* status mix */}
            <Tilt3D radius="1rem" intensity={4} lift={-4} scale={1.006} className="xl:col-span-2">
            <section className="h-full rounded-2xl border border-line bg-porcelain p-5 shadow-card">
              <ChartTitle title="Status mix" sub="How the work is flowing" />
              <div className="mt-2 flex items-center">
                <div className="relative h-[210px] w-[46%] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusMix}
                        dataKey="value"
                        innerRadius={58}
                        outerRadius={82}
                        paddingAngle={4}
                        cornerRadius={6}
                        stroke="#FDFBF6"
                        strokeWidth={3}
                      >
                        {statusMix.map((s) => (
                          <Cell key={s.key} fill={s.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<Tip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 grid place-items-center">
                    <div className="text-center">
                      <div className="font-display text-3xl leading-none font-semibold">
                        <RollingNumber value={filtered.length} delay={0.35} />
                      </div>
                      <div className="mt-1 text-[9px] font-bold tracking-[0.18em] text-ink-faint uppercase">
                        total
                      </div>
                    </div>
                  </div>
                </div>
                <ul className="min-w-0 flex-1 space-y-2.5 pl-2">
                  {statusMix.map((s) => {
                    const pct = filtered.length ? Math.round((s.value / filtered.length) * 100) : 0;
                    return (
                      <li key={s.key}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-2 text-xs font-bold text-ink">
                            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                            <span className="truncate">{s.name}</span>
                          </span>
                          <span className="text-xs font-bold text-ink-soft tabular-nums">
                            {s.value} · {pct}%
                          </span>
                        </div>
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-cream">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: s.color }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          </Tilt3D>
          </div>

          {/* by employee */}
          <Tilt3D radius="1rem" intensity={3} lift={-4} scale={1.004} className="mt-4 block">
            <section className="rounded-2xl border border-line bg-porcelain p-5 shadow-card">
            <ChartTitle title="Load per employee" sub={`Assignments carried · ${label}`} />
            <div className="mt-4" style={{ height: Math.max(180, byEmployee.length * 46) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byEmployee} layout="vertical" margin={{ top: 0, right: 24, left: 12, bottom: 0 }} barCategoryGap={12}>
                  <CartesianGrid horizontal={false} stroke="#E8E0CD" strokeDasharray="3 6" />
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={96}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12, fill: "#221D15", fontWeight: 600 }}
                  />
                  <Tooltip cursor={{ fill: "rgba(34,29,21,0.04)" }} content={<Tip />} />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]} maxBarSize={22}>
                    {byEmployee.map((d) => (
                      <Cell key={d.full} fill={d.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        </Tilt3D>
        </>
      )}
    </div>
  );
}

function ChartTitle({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
      <p className="mt-0.5 text-xs text-ink-faint">{sub}</p>
    </div>
  );
}

type TipProps = {
  active?: boolean;
  payload?: Array<{ value?: number | string }>;
  label?: string;
  labelKey?: string;
};

function Tip({ active, payload, label, labelKey }: TipProps & { [k: string]: unknown }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = (payload[0] as unknown as { payload?: Record<string, unknown> }).payload;
  const caption =
    (labelKey && row && typeof row[labelKey] === "string" ? (row[labelKey] as string) : null) ??
    label ??
    (row && typeof row.full === "string" ? row.full : "") ??
    "";
  return (
    <div className="rounded-xl border border-line bg-porcelain px-3.5 py-2.5 shadow-lifted">
      <div className="max-w-[200px] truncate text-[10px] font-bold tracking-[0.14em] text-ink-faint uppercase">
        {caption}
      </div>
      <div className="mt-0.5 font-display text-xl leading-none font-semibold">
        {payload[0].value}
        <span className="ml-1.5 font-sans text-[10px] font-medium text-ink-soft">itineraries</span>
      </div>
    </div>
  );
}
